'use client';

import type { CSSProperties, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import {
  BriefcaseBusiness,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Globe2,
  Instagram,
  Link2 as LinkIcon,
  Linkedin,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  MoreVertical,
  Music2,
  Phone,
  Search,
  Send,
  Users,
  Youtube,
} from 'lucide-react';
import type {
  PublicProfileContact,
  PublicProfileOwnerKind,
  PublicProfileOwnerSummary,
  PublicProfileRenderBlock,
  PublicProfileRenderOpenRole,
  PublicProfileRenderProject,
  PublicProfileSocial,
} from '@tabbio/types';
import {
  getPublicProfileBlockDefaultLabel,
  getPublicProfileExternalUrl,
} from '@tabbio/types';
import {
  resolvePublicProfilePreviewDesign,
  type PublicProfilePreviewDesignInput,
} from './profile-design';

export type PublicProfileSurfaceLabels = {
  upgrade: string;
  website: string;
  message: string;
  joinOwner: (name: string) => string;
  cookiePreferences: string;
  report: string;
  privacy: string;
  open: string;
  webCv: string;
  servicesCount: (count: number) => string;
  projectsCount: (count: number) => string;
  teamCount: (count: number) => string;
  getDirections: string;
  openMap: string;
  contactForm: string;
  openForm: string;
  openRoles: string;
  searchRoles: string;
  location: string;
  type: string;
  apply: string;
  applyNow: string;
  noRoleMatches: string;
  noOpenRoles: string;
  talentPoolCta: string;
  moreRoleOptions: (title: string) => string;
  contactLabels: Partial<Record<PublicProfileContact['id'], string>>;
  socialLabels: Partial<Record<PublicProfileSocial['id'], string>>;
};

export type PublicProfileSurfaceDocument = {
  ownerKind: PublicProfileOwnerKind;
  owner: PublicProfileOwnerSummary;
  canonicalHandle?: string | null;
  publicUrl: string;
  socials: PublicProfileSocial[];
  contacts: PublicProfileContact[];
  blocks: PublicProfileRenderBlock[];
  tracking?: {
    redirectBaseUrl?: string | null;
  };
};

export type PublicProfileSurfaceProps = {
  document: PublicProfileSurfaceDocument;
  previewDesign?: PublicProfilePreviewDesignInput;
  labels: PublicProfileSurfaceLabels;
  variant?: 'preview' | 'published';
  topSlot?: ReactNode;
  upgradeSlot?: ReactNode;
  beforeHeroSlot?: ReactNode;
  editSlot?: ReactNode;
  footerSlot?: ReactNode;
  footerPrivacyHref?: string;
  onNavigate?: (href: string) => void;
  onOpenMenu?: () => void;
  onUpgrade?: () => void;
  onFooterCta?: () => void;
  onRoleMore?: (role: PublicProfileRenderOpenRole) => void;
  onTalentPool?: (block: Extract<PublicProfileRenderBlock, { type: 'openroles' }>) => void;
  dir?: 'ltr' | 'rtl' | 'auto';
  className?: string;
  style?: CSSProperties;
};

export type { PublicProfilePreviewDesignInput } from './profile-design';

type PublicProfileSurfaceDensity = {
  shellPaddingInline: number;
  shellPaddingTop: number;
  contentGap: number;
  contentPaddingBottom: number;
  socialIconSize: number;
  topChrome: {
    rowGap: number;
    rowPaddingBlock: number;
    rowMarginBottom: number;
    menuSize: number;
    menuIconSize: number;
    upgradeMinHeight: number;
    upgradePaddingInline: number;
    upgradeFontSize: number;
    avatarSize: number;
    avatarFontSize: number;
  };
  hero: {
    paddingTop: number;
    paddingBottom: number;
    paddingInline: number;
    avatarSize: number;
    avatarFontSize: number;
    avatarBorderWidth: number;
    nameMarginTop: number;
    nameFontSize: number;
    nameLineHeight: string;
    headlineMarginTop: number;
    headlineLineHeight: string;
    locationMarginTop: number;
    socialMarginTop: number;
    socialGap: number;
    contactMarginTop: number;
    contactGap: number;
    contactWrap: 'wrap' | 'nowrap';
    contactJustify: 'center' | 'flex-start';
    contactOverflowX: 'visible' | 'auto';
    contactButtonMinHeight: number;
    contactButtonRadius: number;
    contactButtonGap: number;
    contactButtonPaddingInline: number;
    contactButtonFontSize: number;
    contactPrimaryShadow: string;
  };
  cards: {
    shellRadius: number;
    shellShadow: string;
    primaryMinHeight: number;
    primaryGap: number;
    primaryRadius: number;
    primaryPaddingBlock: number;
    primaryPaddingInline: number;
    primaryTitleFontSize: number;
    primaryTitleLineHeight: string;
    primarySubtitleMarginTop: number;
    primarySubtitleFontSize: number;
    primarySubtitleLineHeight: string;
    primaryChevronSize: number;
    sectionHeaderGap: number;
    sectionHeaderPadding: number;
    sectionTitleFontSize: number;
    sectionTitleLineHeight: string;
    sectionSubtitleMarginTop: number;
    sectionSubtitleFontSize: number;
    sectionSubtitleLineHeight: string;
    bodyPaddingInline: number;
    bodyPaddingBottom: number;
    websiteCoverHeight: number;
    websiteRowGap: number;
    websiteRowPadding: number;
    websiteIconSize: number;
    websiteIconRadius: number;
    websiteGlyphSize: number;
    projectRowGap: number;
    projectRowPaddingBlock: number;
    projectRowPaddingInline: number;
    projectImageWidth: number;
    projectImageHeight: number;
    projectImageRadius: number;
    projectTitleFontSize: number;
    projectDescriptionFontSize: number;
    projectDescriptionLineHeight: string;
    teamAvatarSize: number;
    teamNameFontSize: number;
    teamRoleFontSize: number;
    actionButtonMinHeight: number;
    actionButtonRadius: number;
    actionButtonFontSize: number;
    actionButtonPaddingInline: number;
    mapActionButtonSize: number;
  };
  openRoles: {
    sectionGap: number;
    headerGap: number;
    titleFontSize: number;
    titleLineHeight: string;
    countSize: number;
    countFontSize: number;
    filterRadius: number;
    filterShadow: string;
    filterMinHeight: number;
    filterSearchIconSize: number;
    filterSearchPaddingBlock: number;
    filterSearchPaddingStart: number;
    filterSearchPaddingEnd: number;
    filterSelectPaddingStart: number;
    filterSelectPaddingEnd: number;
    filterChevronSize: number;
    rolesGap: number;
    roleCardRadius: number;
    roleCardPadding: number;
    roleCardGap: number;
    roleTitleFontSize: number;
    roleTitleLineHeight: string;
    roleMetaMarginTop: number;
    roleMetaFontSize: number;
    roleMetaLineHeight: string;
    chipMarginTop: number;
    chipGap: number;
    chipPaddingBlock: number;
    chipPaddingInline: number;
    chipFontSize: number;
    actionsGap: number;
    applyMinHeight: number;
    applyRadius: number;
    applyFontSize: number;
    applyPaddingInline: number;
    moreButtonSize: number;
    moreButtonRadius: number;
    moreIconSize: number;
    talentTopPadding: number;
    talentFontSize: number;
    talentLineHeight: string;
    talentButtonMinHeight: number;
    talentButtonPaddingInline: number;
    talentButtonFontSize: number;
    emptyRadius: number;
    emptyPaddingBlock: number;
    emptyPaddingInline: number;
    emptyFontSize: number;
  };
  footer: {
    paddingBlockStart: number;
    paddingBlockEnd: number;
    ctaMinHeight: number;
    ctaRadius: number;
    ctaPaddingInline: number;
    ctaFontSize: number;
    linksMarginTop: number;
    linksGap: number;
    linksFontSize: number;
  };
};

const SURFACE_DENSITY: Record<NonNullable<PublicProfileSurfaceProps['variant']>, PublicProfileSurfaceDensity> = {
  published: {
    shellPaddingInline: 16,
    shellPaddingTop: 32,
    contentGap: 16,
    contentPaddingBottom: 32,
    socialIconSize: 38,
    topChrome: {
      rowGap: 12,
      rowPaddingBlock: 10,
      rowMarginBottom: 14,
      menuSize: 40,
      menuIconSize: 22,
      upgradeMinHeight: 36,
      upgradePaddingInline: 18,
      upgradeFontSize: 14,
      avatarSize: 38,
      avatarFontSize: 14,
    },
    hero: {
      paddingTop: 8,
      paddingBottom: 24,
      paddingInline: 24,
      avatarSize: 88,
      avatarFontSize: 28,
      avatarBorderWidth: 3,
      nameMarginTop: 18,
      nameFontSize: 32,
      nameLineHeight: '38px',
      headlineMarginTop: 10,
      headlineLineHeight: '26px',
      locationMarginTop: 13,
      socialMarginTop: 18,
      socialGap: 12,
      contactMarginTop: 18,
      contactGap: 10,
      contactWrap: 'wrap',
      contactJustify: 'center',
      contactOverflowX: 'visible',
      contactButtonMinHeight: 46,
      contactButtonRadius: 13,
      contactButtonGap: 8,
      contactButtonPaddingInline: 18,
      contactButtonFontSize: 14,
      contactPrimaryShadow: '0 8px 18px rgba(94,41,255,0.18)',
    },
    cards: {
      shellRadius: 28,
      shellShadow: '0 10px 30px rgba(0,0,0,0.04)',
      primaryMinHeight: 92,
      primaryGap: 16,
      primaryRadius: 28,
      primaryPaddingBlock: 18,
      primaryPaddingInline: 22,
      primaryTitleFontSize: 20,
      primaryTitleLineHeight: '28px',
      primarySubtitleMarginTop: 4,
      primarySubtitleFontSize: 15,
      primarySubtitleLineHeight: '22px',
      primaryChevronSize: 21,
      sectionHeaderGap: 12,
      sectionHeaderPadding: 22,
      sectionTitleFontSize: 20,
      sectionTitleLineHeight: '28px',
      sectionSubtitleMarginTop: 4,
      sectionSubtitleFontSize: 15,
      sectionSubtitleLineHeight: '26px',
      bodyPaddingInline: 22,
      bodyPaddingBottom: 22,
      websiteCoverHeight: 210,
      websiteRowGap: 16,
      websiteRowPadding: 22,
      websiteIconSize: 48,
      websiteIconRadius: 15,
      websiteGlyphSize: 21,
      projectRowGap: 16,
      projectRowPaddingBlock: 18,
      projectRowPaddingInline: 22,
      projectImageWidth: 96,
      projectImageHeight: 64,
      projectImageRadius: 15,
      projectTitleFontSize: 17,
      projectDescriptionFontSize: 14,
      projectDescriptionLineHeight: '22px',
      teamAvatarSize: 48,
      teamNameFontSize: 17,
      teamRoleFontSize: 14,
      actionButtonMinHeight: 46,
      actionButtonRadius: 13,
      actionButtonFontSize: 14,
      actionButtonPaddingInline: 20,
      mapActionButtonSize: 44,
    },
    openRoles: {
      sectionGap: 16,
      headerGap: 12,
      titleFontSize: 32,
      titleLineHeight: '38px',
      countSize: 36,
      countFontSize: 14,
      filterRadius: 24,
      filterShadow: '0 10px 30px rgba(0,0,0,0.035)',
      filterMinHeight: 50,
      filterSearchIconSize: 18,
      filterSearchPaddingBlock: 12,
      filterSearchPaddingStart: 44,
      filterSearchPaddingEnd: 12,
      filterSelectPaddingStart: 16,
      filterSelectPaddingEnd: 40,
      filterChevronSize: 18,
      rolesGap: 14,
      roleCardRadius: 28,
      roleCardPadding: 20,
      roleCardGap: 16,
      roleTitleFontSize: 20,
      roleTitleLineHeight: '28px',
      roleMetaMarginTop: 4,
      roleMetaFontSize: 15,
      roleMetaLineHeight: '26px',
      chipMarginTop: 12,
      chipGap: 8,
      chipPaddingBlock: 6,
      chipPaddingInline: 12,
      chipFontSize: 12,
      actionsGap: 8,
      applyMinHeight: 46,
      applyRadius: 13,
      applyFontSize: 14,
      applyPaddingInline: 20,
      moreButtonSize: 46,
      moreButtonRadius: 13,
      moreIconSize: 18,
      talentTopPadding: 20,
      talentFontSize: 15,
      talentLineHeight: '24px',
      talentButtonMinHeight: 44,
      talentButtonPaddingInline: 20,
      talentButtonFontSize: 14,
      emptyRadius: 28,
      emptyPaddingBlock: 38,
      emptyPaddingInline: 20,
      emptyFontSize: 14,
    },
    footer: {
      paddingBlockStart: 24,
      paddingBlockEnd: 48,
      ctaMinHeight: 44,
      ctaRadius: 13,
      ctaPaddingInline: 20,
      ctaFontSize: 14,
      linksMarginTop: 14,
      linksGap: 18,
      linksFontSize: 12,
    },
  },
  preview: {
    shellPaddingInline: 14,
    shellPaddingTop: 4,
    contentGap: 14,
    contentPaddingBottom: 28,
    socialIconSize: 34,
    topChrome: {
      rowGap: 10,
      rowPaddingBlock: 8,
      rowMarginBottom: 10,
      menuSize: 36,
      menuIconSize: 20,
      upgradeMinHeight: 34,
      upgradePaddingInline: 16,
      upgradeFontSize: 13,
      avatarSize: 34,
      avatarFontSize: 13,
    },
    hero: {
      paddingTop: 4,
      paddingBottom: 18,
      paddingInline: 18,
      avatarSize: 80,
      avatarFontSize: 24,
      avatarBorderWidth: 2,
      nameMarginTop: 14,
      nameFontSize: 30,
      nameLineHeight: '36px',
      headlineMarginTop: 8,
      headlineLineHeight: '24px',
      locationMarginTop: 10,
      socialMarginTop: 14,
      socialGap: 10,
      contactMarginTop: 14,
      contactGap: 8,
      contactWrap: 'wrap',
      contactJustify: 'center',
      contactOverflowX: 'visible',
      contactButtonMinHeight: 42,
      contactButtonRadius: 12,
      contactButtonGap: 7,
      contactButtonPaddingInline: 16,
      contactButtonFontSize: 13,
      contactPrimaryShadow: '0 6px 14px rgba(94,41,255,0.16)',
    },
    cards: {
      shellRadius: 24,
      shellShadow: '0 8px 24px rgba(0,0,0,0.035)',
      primaryMinHeight: 84,
      primaryGap: 14,
      primaryRadius: 24,
      primaryPaddingBlock: 15,
      primaryPaddingInline: 18,
      primaryTitleFontSize: 18,
      primaryTitleLineHeight: '26px',
      primarySubtitleMarginTop: 3,
      primarySubtitleFontSize: 14,
      primarySubtitleLineHeight: '21px',
      primaryChevronSize: 19,
      sectionHeaderGap: 10,
      sectionHeaderPadding: 18,
      sectionTitleFontSize: 18,
      sectionTitleLineHeight: '26px',
      sectionSubtitleMarginTop: 3,
      sectionSubtitleFontSize: 14,
      sectionSubtitleLineHeight: '24px',
      bodyPaddingInline: 18,
      bodyPaddingBottom: 18,
      websiteCoverHeight: 188,
      websiteRowGap: 12,
      websiteRowPadding: 18,
      websiteIconSize: 44,
      websiteIconRadius: 13,
      websiteGlyphSize: 19,
      projectRowGap: 14,
      projectRowPaddingBlock: 15,
      projectRowPaddingInline: 18,
      projectImageWidth: 86,
      projectImageHeight: 58,
      projectImageRadius: 13,
      projectTitleFontSize: 16,
      projectDescriptionFontSize: 13,
      projectDescriptionLineHeight: '20px',
      teamAvatarSize: 44,
      teamNameFontSize: 16,
      teamRoleFontSize: 13,
      actionButtonMinHeight: 42,
      actionButtonRadius: 12,
      actionButtonFontSize: 13,
      actionButtonPaddingInline: 18,
      mapActionButtonSize: 40,
    },
    openRoles: {
      sectionGap: 14,
      headerGap: 10,
      titleFontSize: 30,
      titleLineHeight: '36px',
      countSize: 34,
      countFontSize: 13,
      filterRadius: 22,
      filterShadow: '0 8px 24px rgba(0,0,0,0.03)',
      filterMinHeight: 46,
      filterSearchIconSize: 17,
      filterSearchPaddingBlock: 10,
      filterSearchPaddingStart: 40,
      filterSearchPaddingEnd: 10,
      filterSelectPaddingStart: 14,
      filterSelectPaddingEnd: 36,
      filterChevronSize: 17,
      rolesGap: 12,
      roleCardRadius: 24,
      roleCardPadding: 18,
      roleCardGap: 14,
      roleTitleFontSize: 18,
      roleTitleLineHeight: '26px',
      roleMetaMarginTop: 3,
      roleMetaFontSize: 14,
      roleMetaLineHeight: '24px',
      chipMarginTop: 10,
      chipGap: 6,
      chipPaddingBlock: 5,
      chipPaddingInline: 10,
      chipFontSize: 11,
      actionsGap: 6,
      applyMinHeight: 42,
      applyRadius: 12,
      applyFontSize: 13,
      applyPaddingInline: 18,
      moreButtonSize: 42,
      moreButtonRadius: 12,
      moreIconSize: 17,
      talentTopPadding: 16,
      talentFontSize: 14,
      talentLineHeight: '22px',
      talentButtonMinHeight: 42,
      talentButtonPaddingInline: 18,
      talentButtonFontSize: 13,
      emptyRadius: 24,
      emptyPaddingBlock: 32,
      emptyPaddingInline: 18,
      emptyFontSize: 13,
    },
    footer: {
      paddingBlockStart: 20,
      paddingBlockEnd: 38,
      ctaMinHeight: 42,
      ctaRadius: 12,
      ctaPaddingInline: 18,
      ctaFontSize: 13,
      linksMarginTop: 12,
      linksGap: 14,
      linksFontSize: 11,
    },
  },
};

type LinkOptions = {
  kind?: 'cta' | 'block' | 'job';
  source?: string;
  blockId?: string;
  blockType?: string;
  jobId?: string;
};

const TEXT = 'var(--tabbio-text, var(--color-text, #0d0d0d))';
const TEXT_SECONDARY = 'var(--tabbio-text-secondary, var(--color-text-secondary, #5d5d5d))';
const TEXT_TERTIARY = 'var(--tabbio-text-tertiary, var(--color-text-tertiary, #8f8f8f))';
const BORDER_SUBTLE = 'var(--tabbio-border-subtle, var(--color-border-subtle, rgba(0,0,0,0.05)))';
const BORDER = 'var(--tabbio-border, rgba(0,0,0,0.10))';
const SURFACE = 'var(--tabbio-surface, var(--color-surface, #f9f9f9))';
const SURFACE_SECONDARY = 'var(--tabbio-surface-secondary, var(--color-surface-secondary, #ededed))';
const SURFACE_ELEVATED = 'var(--tabbio-surface-elevated, var(--color-surface-elevated, #ffffff))';
const PRIMARY = 'var(--tabbio-button-primary-bg, #5e29ff)';
const PRIMARY_TEXT = 'var(--tabbio-button-primary-text, #ffffff)';
const PRIMARY_BORDER = 'var(--tabbio-button-primary-border, transparent)';
const PREVIEW_ACTION_RADIUS = 'var(--tabbio-preview-action-radius, 999px)';
const PREVIEW_ACTION_BORDER_WIDTH = 'var(--tabbio-preview-action-border-width, 0px)';

function trimToNull(value?: string | null) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function getHandle(document: PublicProfileSurfaceDocument) {
  return document.canonicalHandle || document.owner.slug || document.owner.username || 'profile';
}

function getAvatarFallback(name: string) {
  return name.trim().charAt(0).toUpperCase() || '?';
}

function humanizeValue(value?: string | null) {
  if (!value) return null;
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function displayUrl(value?: string | null) {
  const resolved = getPublicProfileExternalUrl(value);
  if (!resolved) return null;
  try {
    const parsed = new URL(resolved);
    const path = `${parsed.pathname}${parsed.search}`.replace(/\/$/, '');
    return `${parsed.hostname.replace(/^www\./, '')}${path === '/' ? '' : path}`;
  } catch {
    return resolved.replace(/^https?:\/\//, '').replace(/\/$/, '');
  }
}

function MapPreview({
  location,
  interactive,
}: {
  location: string;
  interactive?: boolean;
}) {
  return (
    <iframe
      title={location}
      src={`https://www.google.com/maps?q=${encodeURIComponent(location)}&output=embed`}
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      style={{
        width: '100%',
        height: 260,
        border: 0,
        display: 'block',
        pointerEvents: interactive ? 'auto' : 'none',
      }}
    />
  );
}

function normalizePhoneNumber(value: string) {
  return value.trim().replace(/[^\d+]/g, '');
}

function getContactHref(contact: PublicProfileContact) {
  const value = contact.value.trim();
  if (!value) return null;
  if (contact.id === 'email') return `mailto:${value}`;
  if (contact.id === 'phone' || contact.id === 'landline') return `tel:${normalizePhoneNumber(value)}`;
  if (contact.id === 'whatsapp') {
    const phone = normalizePhoneNumber(value).replace(/^\+/, '');
    return phone ? `https://wa.me/${phone}` : getPublicProfileExternalUrl(value);
  }
  if (contact.id === 'telegram') {
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    const handle = value.replace(/^@/, '');
    return handle ? `https://t.me/${handle}` : null;
  }
  if (contact.id === 'tabbioMessage') return value.startsWith('http') ? value : null;
  return getPublicProfileExternalUrl(value) || value;
}

function buildHref(
  document: PublicProfileSurfaceDocument,
  target: string,
  options?: LinkOptions,
) {
  const redirectBaseUrl = document.tracking?.redirectBaseUrl;
  if (!redirectBaseUrl) return target;
  const url = new URL(redirectBaseUrl);
  url.searchParams.set('target', target);
  if (options?.kind) url.searchParams.set('kind', options.kind);
  if (options?.source) url.searchParams.set('source', options.source);
  if (options?.blockId) url.searchParams.set('blockId', options.blockId);
  if (options?.blockType) url.searchParams.set('blockType', options.blockType);
  if (options?.jobId) url.searchParams.set('jobId', options.jobId);
  return url.toString();
}

function getSectionAnchor(type: PublicProfileRenderBlock['type']) {
  if (type === 'openroles') return 'open-roles';
  if (type === 'team') return 'team';
  return undefined;
}

function NavigateLink({
  href,
  onNavigate,
  children,
  style,
  className,
  label,
}: {
  href: string;
  onNavigate?: (href: string) => void;
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
  label?: string;
}) {
  if (onNavigate) {
    return (
      <button
        type="button"
        aria-label={label}
        onClick={() => onNavigate(href)}
        className={className}
        style={{
          ...style,
          width: style?.width ?? '100%',
          border: style?.border ?? 'none',
          font: 'inherit',
          textAlign: 'inherit',
          cursor: 'pointer',
        }}
      >
        {children}
      </button>
    );
  }

  return (
    <a href={href} aria-label={label} className={className} style={style}>
      {children}
    </a>
  );
}

function TabbioGlyph({ style }: { style?: CSSProperties }) {
  return (
    <svg viewBox="0 0 105.81 123.09" fill="currentColor" aria-hidden="true" style={style}>
      <path d="M65.07,99.14v-33.31h29.9v-23.95h-29.9V0H22.24L0,22.24h0v43.59h41.11v29.2c0,15.5,14.88,28.06,33.23,28.06h31.46v-23.95h-40.74ZM8.68,41.87v-16.49h16.35V8.4h16.09v33.48H8.68Z" />
    </svg>
  );
}

function SocialIcon({ id }: { id: PublicProfileSocial['id'] }) {
  const common = { size: 24, strokeWidth: 1.9 } as const;
  if (id === 'linkedin') return <Linkedin {...common} />;
  if (id === 'instagram') return <Instagram {...common} />;
  if (id === 'youtube') return <Youtube {...common} />;
  if (id === 'tiktok' || id === 'spotify') return <Music2 {...common} />;
  if (id === 'email') return <Mail {...common} />;
  if (id === 'whatsapp') return <MessageCircle {...common} />;
  if (id === 'tabbio') return <TabbioGlyph style={{ width: 22, height: 22 }} />;
  return <span aria-hidden="true" style={{ fontSize: 20, fontWeight: 650, lineHeight: 1 }}>{id === 'x' ? 'X' : id.charAt(0).toUpperCase()}</span>;
}

function ContactIcon({ id }: { id: PublicProfileContact['id'] }) {
  if (id === 'email') return <Mail size={18} strokeWidth={2} />;
  if (id === 'phone' || id === 'landline') return <Phone size={18} strokeWidth={2} />;
  if (id === 'telegram') return <Send size={18} strokeWidth={2} />;
  if (id === 'tabbioMessage') return <TabbioGlyph style={{ width: 18, height: 18 }} />;
  return <MessageCircle size={18} strokeWidth={2} />;
}

function PublicProfileTopChrome({
  document,
  labels,
  density,
  upgradeSlot,
  onOpenMenu,
  onUpgrade,
}: {
  document: PublicProfileSurfaceDocument;
  labels: PublicProfileSurfaceLabels;
  density: PublicProfileSurfaceDensity;
  upgradeSlot?: ReactNode;
  onOpenMenu?: () => void;
  onUpgrade?: () => void;
}) {
  const avatarUrl = document.owner.avatarUrl;
  const name = document.owner.name || getHandle(document);
  const topChromeDensity = density.topChrome;
  const menuStyle: CSSProperties = {
    width: topChromeDensity.menuSize,
    height: topChromeDensity.menuSize,
    borderRadius: 999,
    border: 'none',
    display: 'grid',
    placeItems: 'center',
    color: TEXT_SECONDARY,
    background: 'transparent',
  };
  const upgradeStyle: CSSProperties = {
    minHeight: topChromeDensity.upgradeMinHeight,
    borderRadius: 999,
    border: `1px solid ${BORDER_SUBTLE}`,
    background: SURFACE_ELEVATED,
    color: PRIMARY,
    paddingInline: topChromeDensity.upgradePaddingInline,
    fontSize: topChromeDensity.upgradeFontSize,
    fontWeight: 650,
    boxShadow: '0 8px 20px rgba(94,41,255,0.10)',
  };
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: topChromeDensity.rowGap,
        paddingBlock: topChromeDensity.rowPaddingBlock,
        marginBlockEnd: topChromeDensity.rowMarginBottom,
      }}
    >
      {onOpenMenu ? (
        <button
          type="button"
          onClick={onOpenMenu}
          aria-label={labels.socialLabels.tabbio}
          style={{ ...menuStyle, cursor: 'pointer' }}
        >
          <Menu size={topChromeDensity.menuIconSize} strokeWidth={1.9} />
        </button>
      ) : (
        <div aria-hidden="true" style={menuStyle}>
          <Menu size={topChromeDensity.menuIconSize} strokeWidth={1.9} />
        </div>
      )}
      {upgradeSlot ?? (onUpgrade ? (
        <button type="button" onClick={onUpgrade} style={{ ...upgradeStyle, cursor: 'pointer' }}>
          {labels.upgrade}
        </button>
      ) : (
        <div style={{ ...upgradeStyle, display: 'inline-flex', alignItems: 'center' }}>
          {labels.upgrade}
        </div>
      ))}
      <div
        style={{
          width: topChromeDensity.avatarSize,
          height: topChromeDensity.avatarSize,
          borderRadius: 999,
          overflow: 'hidden',
          display: 'grid',
          placeItems: 'center',
          color: TEXT_SECONDARY,
          background: SURFACE_SECONDARY,
          fontSize: topChromeDensity.avatarFontSize,
          fontWeight: 700,
        }}
      >
        {avatarUrl ? <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : getAvatarFallback(name)}
      </div>
    </div>
  );
}

function PublicProfileAvatar({
  document,
  density,
}: {
  document: PublicProfileSurfaceDocument;
  density: PublicProfileSurfaceDensity;
}) {
  const avatarUrl = document.owner.avatarUrl;
  const name = document.owner.name || getHandle(document);
  const heroDensity = density.hero;
  return (
    <div
      style={{
        marginInline: 'auto',
        width: heroDensity.avatarSize,
        height: heroDensity.avatarSize,
        borderRadius: 999,
        overflow: 'hidden',
        display: 'grid',
        placeItems: 'center',
        background: SURFACE_SECONDARY,
        color: TEXT_SECONDARY,
        fontSize: heroDensity.avatarFontSize,
        fontWeight: 750,
        border: `${heroDensity.avatarBorderWidth}px solid ${SURFACE_ELEVATED}`,
        boxShadow: '0 12px 30px rgba(0,0,0,0.14)',
      }}
    >
      {avatarUrl ? <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : getAvatarFallback(name)}
    </div>
  );
}

function PublicProfileHero({
  document,
  labels,
  density,
  onNavigate,
}: {
  document: PublicProfileSurfaceDocument;
  labels: PublicProfileSurfaceLabels;
  density: PublicProfileSurfaceDensity;
  onNavigate?: (href: string) => void;
}) {
  const heroDensity = density.hero;
  const socialIconSize = density.socialIconSize;
  // `owner.headline` (company.tagline) and `owner.bio` (company.description)
  // are two distinct stored fields, both returned by the API.
  //
  // An individual profile renders both: `headline` is their one line and `bio`
  // the paragraph under it, and a profile with only a bio keeps it in the
  // prominent slot. (Collapsing the two into `headline || bio` used to make
  // setting a headline look like it DELETED the bio - nothing was lost on
  // write, the read side just stopped rendering the second field.)
  //
  // A company page renders the tagline only. The long description no longer has
  // an editor, so showing it would strand copy the employer cannot change; the
  // stored value is left untouched for a future About block.
  const isCompany = document.ownerKind === 'company';
  const taglineText = trimToNull(document.owner.headline);
  const descriptionText = isCompany ? null : trimToNull(document.owner.bio);
  const headline = taglineText ?? descriptionText;
  const description =
    taglineText && descriptionText && descriptionText !== taglineText
      ? descriptionText
      : null;
  const websiteHref = getPublicProfileExternalUrl(document.owner.website);
  const visibleSocials = document.socials
    .filter((social) => social.enabled && social.url.trim() && social.id !== 'tabbio' && social.id !== 'email' && social.id !== 'whatsapp')
    .sort((left, right) => left.order - right.order);
  const visibleContacts = document.contacts
    .filter((contact) => contact.enabled && contact.value.trim())
    .sort((left, right) => left.order - right.order)
    .slice(0, 3);

  return (
    <header
      style={{
        textAlign: 'center',
        paddingBlockStart: heroDensity.paddingTop,
        paddingBlockEnd: heroDensity.paddingBottom,
        paddingInline: heroDensity.paddingInline,
      }}
    >
      <PublicProfileAvatar document={document} density={density} />
      <h1
        dir="auto"
        style={{
          margin: `${heroDensity.nameMarginTop}px auto 0`,
          maxWidth: 680,
          color: TEXT,
          fontSize: heroDensity.nameFontSize,
          lineHeight: heroDensity.nameLineHeight,
          fontWeight: 680,
          letterSpacing: -0.4,
          overflowWrap: 'anywhere',
        }}
      >
        {document.owner.name}
      </h1>
      {headline ? (
        <p
          dir="auto"
          style={{
            margin: `${heroDensity.headlineMarginTop}px auto 0`,
            maxWidth: 620,
            color: TEXT_SECONDARY,
            fontSize: 15,
            lineHeight: heroDensity.headlineLineHeight,
          }}
        >
          {headline}
        </p>
      ) : null}
      {description ? (
        <p
          dir="auto"
          style={{
            margin: `${heroDensity.headlineMarginTop}px auto 0`,
            maxWidth: 620,
            color: TEXT_TERTIARY,
            fontSize: 14,
            lineHeight: heroDensity.headlineLineHeight,
            whiteSpace: 'pre-wrap',
          }}
        >
          {description}
        </p>
      ) : null}
      {document.owner.location ? (
        <div
          dir="auto"
          style={{
            marginBlockStart: heroDensity.locationMarginTop,
            display: 'inline-flex',
            maxWidth: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            color: TEXT_SECONDARY,
            fontSize: 14,
            lineHeight: '22px',
          }}
        >
          <MapPin size={15} strokeWidth={1.8} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{document.owner.location}</span>
        </div>
      ) : null}
      {websiteHref || visibleSocials.length ? (
        <div
          style={{
            marginBlockStart: heroDensity.socialMarginTop,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: heroDensity.socialGap,
          }}
        >
          {websiteHref ? (
            <NavigateLink
              href={buildHref(document, websiteHref, { kind: 'cta', source: 'owner_website' })}
              onNavigate={onNavigate}
              label={labels.website}
              style={getIconLinkStyle(socialIconSize)}
            >
              <Globe2 size={24} strokeWidth={1.9} />
            </NavigateLink>
          ) : null}
          {visibleSocials.map((social) => {
            const socialHref = getPublicProfileExternalUrl(social.url) || social.url;
            const label = labels.socialLabels[social.id] || humanizeValue(social.id) || social.id;
            return (
              <NavigateLink
                key={`${social.id}-${social.url}`}
                href={buildHref(document, socialHref, { kind: 'cta', source: 'social' })}
                onNavigate={onNavigate}
                label={label}
                style={getIconLinkStyle(socialIconSize)}
              >
                <SocialIcon id={social.id} />
              </NavigateLink>
            );
          })}
        </div>
      ) : null}
      {visibleContacts.length ? (
        <div
          style={{
            marginBlockStart: heroDensity.contactMarginTop,
            display: 'flex',
            flexWrap: heroDensity.contactWrap,
            justifyContent: heroDensity.contactJustify,
            gap: heroDensity.contactGap,
            overflowX: heroDensity.contactOverflowX,
            maxWidth: '100%',
            marginInline: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {visibleContacts.map((contact, index) => {
            const href = getContactHref(contact);
            if (!href) return null;
            const isPrimary = index === 0 || contact.id === 'tabbioMessage';
            const label = labels.contactLabels[contact.id] || humanizeValue(contact.id) || labels.message;
            return (
              <NavigateLink
                key={`${contact.id}-${contact.value}`}
                href={buildHref(document, href, { kind: 'cta', source: 'contact' })}
                onNavigate={onNavigate}
                style={{
                  width: 'auto',
                  display: 'inline-flex',
                  minHeight: heroDensity.contactButtonMinHeight,
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: heroDensity.contactButtonGap,
                  borderRadius: PREVIEW_ACTION_RADIUS,
                  border: isPrimary
                    ? `${PREVIEW_ACTION_BORDER_WIDTH} solid ${PRIMARY_BORDER}`
                    : `1px solid ${BORDER}`,
                  background: isPrimary ? PRIMARY : SURFACE_ELEVATED,
                  color: isPrimary ? PRIMARY_TEXT : TEXT,
                  paddingInline: heroDensity.contactButtonPaddingInline,
                  textDecoration: 'none',
                  fontSize: heroDensity.contactButtonFontSize,
                  fontWeight: 650,
                  boxShadow: isPrimary ? heroDensity.contactPrimaryShadow : 'none',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {isPrimary ? <ContactIcon id={contact.id} /> : null}
                {label}
              </NavigateLink>
            );
          })}
        </div>
      ) : null}
    </header>
  );
}

function getIconLinkStyle(size: number): CSSProperties {
  return {
    width: size,
    height: size,
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: TEXT_SECONDARY,
    textDecoration: 'none',
    background: 'transparent',
  };
}

function CardShell({
  children,
  density,
  id,
  style,
}: {
  children: ReactNode;
  density: PublicProfileSurfaceDensity;
  id?: string;
  style?: CSSProperties;
}) {
  const cardDensity = density.cards;
  return (
    <section
      id={id}
      style={{
        overflow: 'hidden',
        borderRadius: cardDensity.shellRadius,
        border: `1px solid ${BORDER_SUBTLE}`,
        background: SURFACE_ELEVATED,
        boxShadow: cardDensity.shellShadow,
        ...style,
      }}
    >
      {children}
    </section>
  );
}

function PrimaryLinkCard({
  document,
  block,
  title,
  subtitle,
  href,
  density,
  onNavigate,
}: {
  document: PublicProfileSurfaceDocument;
  block: PublicProfileRenderBlock;
  title: string;
  subtitle?: string | null;
  href: string;
  density: PublicProfileSurfaceDensity;
  onNavigate?: (href: string) => void;
}) {
  const cardDensity = density.cards;
  return (
    <NavigateLink
      href={buildHref(document, href, { kind: 'block', source: block.type, blockId: block.id, blockType: block.type })}
      onNavigate={onNavigate}
      style={{
        display: 'flex',
        minHeight: cardDensity.primaryMinHeight,
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: cardDensity.primaryGap,
        borderRadius: cardDensity.primaryRadius,
        background: PRIMARY,
        color: PRIMARY_TEXT,
        paddingBlock: cardDensity.primaryPaddingBlock,
        paddingInline: cardDensity.primaryPaddingInline,
        textDecoration: 'none',
        boxShadow: '0 1px 0 rgba(255,255,255,0.18) inset',
      }}
    >
      <span style={{ minWidth: 0, textAlign: 'start' }}>
        <span dir="auto" style={{ display: 'block', fontSize: cardDensity.primaryTitleFontSize, lineHeight: cardDensity.primaryTitleLineHeight, fontWeight: 700 }}>{title}</span>
        {subtitle ? <span dir="auto" style={{ display: 'block', marginBlockStart: cardDensity.primarySubtitleMarginTop, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: TEXT_TERTIARY, fontSize: cardDensity.primarySubtitleFontSize, lineHeight: cardDensity.primarySubtitleLineHeight }}>{subtitle}</span> : null}
      </span>
      <ChevronRight size={cardDensity.primaryChevronSize} strokeWidth={2.2} style={{ flexShrink: 0, opacity: 0.85 }} />
    </NavigateLink>
  );
}

function SectionCard({
  title,
  subtitle,
  icon,
  children,
  density,
  id,
}: {
  title: string;
  subtitle?: string | null;
  icon?: ReactNode;
  children?: ReactNode;
  density: PublicProfileSurfaceDensity;
  id?: string;
}) {
  const cardDensity = density.cards;
  return (
    <CardShell density={density} id={id}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: cardDensity.sectionHeaderGap, padding: cardDensity.sectionHeaderPadding }}>
        {icon ? <div style={{ marginBlockStart: 2, flexShrink: 0, color: PRIMARY }}>{icon}</div> : null}
        <div style={{ minWidth: 0 }}>
          <h2 dir="auto" style={{ margin: 0, color: TEXT, fontSize: cardDensity.sectionTitleFontSize, lineHeight: cardDensity.sectionTitleLineHeight, fontWeight: 700, overflowWrap: 'anywhere' }}>{title}</h2>
          {subtitle ? <p dir="auto" style={{ margin: `${cardDensity.sectionSubtitleMarginTop}px 0 0`, color: TEXT_SECONDARY, fontSize: cardDensity.sectionSubtitleFontSize, lineHeight: cardDensity.sectionSubtitleLineHeight }}>{subtitle}</p> : null}
        </div>
      </div>
      {children}
    </CardShell>
  );
}

function WebsiteCard({
  document,
  block,
  labels,
  density,
  onNavigate,
}: {
  document: PublicProfileSurfaceDocument;
  block: Extract<PublicProfileRenderBlock, { type: 'website' }>;
  labels: PublicProfileSurfaceLabels;
  density: PublicProfileSurfaceDensity;
  onNavigate?: (href: string) => void;
}) {
  const cardDensity = density.cards;
  if (!block.href) return null;
  return (
    <NavigateLink
      href={buildHref(document, block.href, { kind: 'block', source: 'website', blockId: block.id, blockType: block.type })}
      onNavigate={onNavigate}
      style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}
    >
      <CardShell density={density}>
        {document.owner.coverImageUrl ? <img src={document.owner.coverImageUrl} alt="" style={{ width: '100%', height: cardDensity.websiteCoverHeight, objectFit: 'cover', display: 'block' }} /> : null}
        <div style={{ display: 'flex', alignItems: 'center', gap: cardDensity.websiteRowGap, padding: cardDensity.websiteRowPadding }}>
          <div style={{ width: cardDensity.websiteIconSize, height: cardDensity.websiteIconSize, borderRadius: cardDensity.websiteIconRadius, flexShrink: 0, display: 'grid', placeItems: 'center', color: PRIMARY, background: SURFACE_SECONDARY }}>
            <Globe2 size={cardDensity.websiteGlyphSize} strokeWidth={1.9} />
          </div>
          <div style={{ minWidth: 0, flex: 1, textAlign: 'start' }}>
            <div dir="auto" style={{ color: TEXT, fontSize: cardDensity.primaryTitleFontSize, lineHeight: cardDensity.primaryTitleLineHeight, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{block.title || labels.website}</div>
            <div dir="auto" style={{ color: TEXT_SECONDARY, fontSize: cardDensity.primarySubtitleFontSize, lineHeight: cardDensity.primarySubtitleLineHeight, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{block.domain || displayUrl(block.href)}</div>
          </div>
          <ChevronRight size={cardDensity.primaryChevronSize} strokeWidth={2.2} style={{ flexShrink: 0, color: TEXT_SECONDARY }} />
        </div>
      </CardShell>
    </NavigateLink>
  );
}

function ProjectRow({
  project,
  document,
  block,
  density,
  onNavigate,
}: {
  project: PublicProfileRenderProject;
  document: PublicProfileSurfaceDocument;
  block: PublicProfileRenderBlock;
  density: PublicProfileSurfaceDensity;
  onNavigate?: (href: string) => void;
}) {
  const cardDensity = density.cards;
  const row = (
    <div style={{ display: 'flex', alignItems: 'center', gap: cardDensity.projectRowGap, background: SURFACE_ELEVATED, paddingBlock: cardDensity.projectRowPaddingBlock, paddingInline: cardDensity.projectRowPaddingInline }}>
      {project.imageUrl ? <img src={project.imageUrl} alt="" style={{ width: cardDensity.projectImageWidth, height: cardDensity.projectImageHeight, borderRadius: cardDensity.projectImageRadius, objectFit: 'cover', flexShrink: 0 }} /> : <div style={{ width: cardDensity.projectImageWidth, height: cardDensity.projectImageHeight, borderRadius: cardDensity.projectImageRadius, background: SURFACE_SECONDARY, display: 'grid', placeItems: 'center', flexShrink: 0, color: PRIMARY }}><BriefcaseBusiness size={20} strokeWidth={1.8} /></div>}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div dir="auto" style={{ color: TEXT, fontSize: cardDensity.projectTitleFontSize, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{project.title}</div>
        {project.description ? <p dir="auto" style={{ margin: '4px 0 0', color: TEXT_SECONDARY, fontSize: cardDensity.projectDescriptionFontSize, lineHeight: cardDensity.projectDescriptionLineHeight, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{project.description}</p> : null}
      </div>
      {project.href ? <ChevronRight size={cardDensity.primaryChevronSize - 1} strokeWidth={2.2} style={{ flexShrink: 0, color: TEXT_SECONDARY }} /> : null}
    </div>
  );

  if (!project.href) return row;
  return (
    <NavigateLink
      href={buildHref(document, project.href, { kind: 'block', source: 'portfolio', blockId: block.id, blockType: block.type })}
      onNavigate={onNavigate}
      style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
    >
      {row}
    </NavigateLink>
  );
}

function PublicProfileOpenRolesSection({
  document,
  block,
  labels,
  density,
  onNavigate,
  onRoleMore,
  onTalentPool,
}: {
  document: PublicProfileSurfaceDocument;
  block: Extract<PublicProfileRenderBlock, { type: 'openroles' }>;
  labels: PublicProfileSurfaceLabels;
  density: PublicProfileSurfaceDensity;
  onNavigate?: (href: string) => void;
  onRoleMore?: (role: PublicProfileRenderOpenRole) => void;
  onTalentPool?: (block: Extract<PublicProfileRenderBlock, { type: 'openroles' }>) => void;
}) {
  const openRolesDensity = density.openRoles;
  const roles = block.roles;
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const uniqueLocations = useMemo(() => [...new Set(roles.map((role) => role.location).filter(Boolean))] as string[], [roles]);
  const uniqueTypes = useMemo(() => [...new Set(roles.map((role) => role.type).filter(Boolean))] as string[], [roles]);
  const filteredRoles = useMemo(() => {
    let result = roles;
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      result = result.filter((role) => [role.title, role.department, role.location, role.type, role.level, role.workMode].filter(Boolean).join(' ').toLowerCase().includes(query));
    }
    if (locationFilter) result = result.filter((role) => role.location === locationFilter);
    if (typeFilter) result = result.filter((role) => role.type === typeFilter);
    return result;
  }, [roles, searchQuery, locationFilter, typeFilter]);

  return (
    <section id={getSectionAnchor(block.type)} style={{ display: 'grid', gap: openRolesDensity.sectionGap }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: openRolesDensity.headerGap }}>
        <h2 style={{ margin: 0, color: TEXT, fontSize: openRolesDensity.titleFontSize, lineHeight: openRolesDensity.titleLineHeight, fontWeight: 700 }}>{block.title || labels.openRoles}</h2>
        <span style={{ minWidth: openRolesDensity.countSize, height: openRolesDensity.countSize, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 999, background: SURFACE_SECONDARY, color: TEXT_TERTIARY, fontSize: openRolesDensity.countFontSize, fontWeight: 650 }}>{filteredRoles.length}</span>
      </div>
      <CardShell density={density} style={{ borderRadius: openRolesDensity.filterRadius, boxShadow: openRolesDensity.filterShadow }}>
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          <label style={{ position: 'relative', display: 'flex', minHeight: openRolesDensity.filterMinHeight, minWidth: 0, flex: 1.55, alignItems: 'center' }}>
            <span style={srOnlyStyle}>{labels.searchRoles}</span>
            <Search size={openRolesDensity.filterSearchIconSize} strokeWidth={1.8} style={{ position: 'absolute', insetInlineStart: 16, top: '50%', transform: 'translateY(-50%)', color: TEXT_TERTIARY, pointerEvents: 'none' }} />
            <input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder={labels.searchRoles} style={{ width: '100%', height: '100%', border: 'none', outline: 'none', background: 'transparent', color: TEXT, paddingBlock: openRolesDensity.filterSearchPaddingBlock, paddingInlineStart: openRolesDensity.filterSearchPaddingStart, paddingInlineEnd: openRolesDensity.filterSearchPaddingEnd, fontSize: 15, fontWeight: 500 }} />
          </label>
          <RoleFilterSelect value={locationFilter} onChange={setLocationFilter} label={labels.location} options={uniqueLocations} density={density} />
          <RoleFilterSelect value={typeFilter} onChange={setTypeFilter} label={labels.type} options={uniqueTypes} density={density} />
        </div>
      </CardShell>
      <div style={{ display: 'grid', gap: openRolesDensity.rolesGap }}>
        {filteredRoles.map((role) => (
          <RoleCard
            key={role.id}
            role={role}
            document={document}
            labels={labels}
            density={density}
            onNavigate={onNavigate}
            onRoleMore={onRoleMore}
          />
        ))}
      </div>
      {filteredRoles.length === 0 && roles.length > 0 ? <EmptyCard density={density}>{labels.noRoleMatches}</EmptyCard> : null}
      {roles.length === 0 ? <EmptyCard density={density}>{labels.noOpenRoles}</EmptyCard> : null}
      {block.talentPoolEnabled !== false && roles.length > 0 ? (
        <div style={{ borderBlockStart: `1px solid ${BORDER_SUBTLE}`, paddingBlockStart: openRolesDensity.talentTopPadding, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ minWidth: 0, flex: 1, color: TEXT_SECONDARY, fontSize: openRolesDensity.talentFontSize, lineHeight: openRolesDensity.talentLineHeight }}>{labels.talentPoolCta}</span>
          {onTalentPool ? (
            <button type="button" onClick={() => onTalentPool(block)} style={{ minHeight: openRolesDensity.talentButtonMinHeight, borderRadius: 999, border: `1px solid ${BORDER}`, background: SURFACE_ELEVATED, color: TEXT, paddingInline: openRolesDensity.talentButtonPaddingInline, fontSize: openRolesDensity.talentButtonFontSize, fontWeight: 650, cursor: 'pointer' }}>{labels.applyNow}</button>
          ) : (
            <span style={{ minHeight: openRolesDensity.talentButtonMinHeight, borderRadius: 999, border: `1px solid ${BORDER}`, background: SURFACE_ELEVATED, color: TEXT, paddingInline: openRolesDensity.talentButtonPaddingInline, fontSize: openRolesDensity.talentButtonFontSize, fontWeight: 650, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{labels.applyNow}</span>
          )}
        </div>
      ) : null}
    </section>
  );
}

function RoleFilterSelect({
  value,
  onChange,
  label,
  options,
  density,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  options: string[];
  density: PublicProfileSurfaceDensity;
}) {
  const openRolesDensity = density.openRoles;
  return (
    <label style={{ position: 'relative', display: 'flex', minHeight: openRolesDensity.filterMinHeight, minWidth: 0, flex: 1, alignItems: 'center', borderInlineStart: `1px solid ${BORDER_SUBTLE}` }}>
      <span style={srOnlyStyle}>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)} style={{ width: '100%', height: '100%', appearance: 'none', border: 'none', outline: 'none', background: 'transparent', color: TEXT_SECONDARY, paddingInlineStart: openRolesDensity.filterSelectPaddingStart, paddingInlineEnd: openRolesDensity.filterSelectPaddingEnd, fontSize: 15, fontWeight: 600 }}>
        <option value="">{label}</option>
        {options.map((option) => <option key={option} value={option}>{humanizeValue(option)}</option>)}
      </select>
      <ChevronDown size={openRolesDensity.filterChevronSize} strokeWidth={1.8} style={{ position: 'absolute', insetInlineEnd: 16, top: '50%', transform: 'translateY(-50%)', color: TEXT_SECONDARY, pointerEvents: 'none' }} />
    </label>
  );
}

function RoleCard({
  role,
  document,
  labels,
  density,
  onNavigate,
  onRoleMore,
}: {
  role: PublicProfileRenderOpenRole;
  document: PublicProfileSurfaceDocument;
  labels: PublicProfileSurfaceLabels;
  density: PublicProfileSurfaceDensity;
  onNavigate?: (href: string) => void;
  onRoleMore?: (role: PublicProfileRenderOpenRole) => void;
}) {
  const openRolesDensity = density.openRoles;
  const roleHref = role.canonicalUrl ?? `/jobs/${role.publicSlug ?? role.id}`;
  const chips = [role.type, role.level, role.workMode].filter(Boolean) as string[];
  return (
    <article style={{ borderRadius: openRolesDensity.roleCardRadius, border: `1px solid ${BORDER_SUBTLE}`, background: SURFACE_ELEVATED, padding: openRolesDensity.roleCardPadding, boxShadow: density.cards.shellShadow }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: openRolesDensity.roleCardGap }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <NavigateLink href={buildHref(document, roleHref, { kind: 'job', source: 'open_roles', jobId: role.id })} onNavigate={onNavigate} style={{ display: 'block', color: TEXT, textDecoration: 'none', fontSize: openRolesDensity.roleTitleFontSize, lineHeight: openRolesDensity.roleTitleLineHeight, fontWeight: 700, overflowWrap: 'anywhere' }}>
            <span dir="auto">{role.title}</span>
          </NavigateLink>
          <div dir="auto" style={{ marginBlockStart: openRolesDensity.roleMetaMarginTop, color: TEXT_SECONDARY, fontSize: openRolesDensity.roleMetaFontSize, lineHeight: openRolesDensity.roleMetaLineHeight }}>{[role.department, role.location].filter(Boolean).join(' · ')}</div>
          {chips.length ? (
            <div style={{ marginBlockStart: openRolesDensity.chipMarginTop, display: 'flex', flexWrap: 'wrap', gap: openRolesDensity.chipGap }}>
              {chips.map((chip) => <span key={chip} style={{ borderRadius: 999, border: `1px solid ${BORDER_SUBTLE}`, background: SURFACE_SECONDARY, color: TEXT_SECONDARY, paddingBlock: openRolesDensity.chipPaddingBlock, paddingInline: openRolesDensity.chipPaddingInline, fontSize: openRolesDensity.chipFontSize, fontWeight: 650, lineHeight: 1 }}>{humanizeValue(chip)}</span>)}
            </div>
          ) : null}
        </div>
        <div style={{ display: 'flex', flexShrink: 0, alignItems: 'center', gap: openRolesDensity.actionsGap }}>
          <NavigateLink href={buildHref(document, role.applyUrl ?? roleHref, { kind: 'job', source: 'open_roles_apply', jobId: role.id })} onNavigate={onNavigate} style={{ display: 'inline-flex', minHeight: openRolesDensity.applyMinHeight, alignItems: 'center', justifyContent: 'center', borderRadius: PREVIEW_ACTION_RADIUS, border: `${PREVIEW_ACTION_BORDER_WIDTH} solid ${PRIMARY_BORDER}`, background: PRIMARY, color: PRIMARY_TEXT, paddingInline: openRolesDensity.applyPaddingInline, textDecoration: 'none', fontSize: openRolesDensity.applyFontSize, fontWeight: 700 }}>
            {labels.apply}
          </NavigateLink>
          {onRoleMore ? (
            <button type="button" onClick={() => onRoleMore(role)} aria-label={labels.moreRoleOptions(role.title)} style={{ width: openRolesDensity.moreButtonSize, height: openRolesDensity.moreButtonSize, borderRadius: openRolesDensity.moreButtonRadius, border: `1px solid ${BORDER}`, background: SURFACE_ELEVATED, color: TEXT_SECONDARY, display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
              <MoreVertical size={openRolesDensity.moreIconSize} strokeWidth={2} />
            </button>
          ) : (
            <NavigateLink href={buildHref(document, roleHref, { kind: 'job', source: 'open_roles_more', jobId: role.id })} onNavigate={onNavigate} label={labels.moreRoleOptions(role.title)} style={{ width: openRolesDensity.moreButtonSize, height: openRolesDensity.moreButtonSize, borderRadius: openRolesDensity.moreButtonRadius, border: `1px solid ${BORDER}`, background: SURFACE_ELEVATED, color: TEXT_SECONDARY, display: 'grid', placeItems: 'center', textDecoration: 'none' }}>
              <MoreVertical size={openRolesDensity.moreIconSize} strokeWidth={2} />
            </NavigateLink>
          )}
        </div>
      </div>
    </article>
  );
}

function EmptyCard({ children, density }: { children: ReactNode; density: PublicProfileSurfaceDensity }) {
  const openRolesDensity = density.openRoles;
  return <div style={{ borderRadius: openRolesDensity.emptyRadius, border: `1px dashed ${BORDER}`, background: SURFACE_SECONDARY, paddingBlock: openRolesDensity.emptyPaddingBlock, paddingInline: openRolesDensity.emptyPaddingInline, textAlign: 'center', color: TEXT_TERTIARY, fontSize: openRolesDensity.emptyFontSize }}>{children}</div>;
}

function renderBlockSection(
  block: PublicProfileRenderBlock,
  document: PublicProfileSurfaceDocument,
  labels: PublicProfileSurfaceLabels,
  variant: NonNullable<PublicProfileSurfaceProps['variant']>,
  density: PublicProfileSurfaceDensity,
  onNavigate?: (href: string) => void,
  onRoleMore?: (role: PublicProfileRenderOpenRole) => void,
  onTalentPool?: (block: Extract<PublicProfileRenderBlock, { type: 'openroles' }>) => void,
) {
  const cardDensity = density.cards;
  const title = block.title || getPublicProfileBlockDefaultLabel(block.type);
  switch (block.type) {
    case 'webcv':
      return block.href ? <PrimaryLinkCard key={block.id} document={document} block={block} title={title === 'Career profile' ? labels.webCv : title} subtitle={displayUrl(block.href)} href={block.href} density={density} onNavigate={onNavigate} /> : null;
    case 'website':
      return <WebsiteCard key={block.id} document={document} block={block} labels={labels} density={density} onNavigate={onNavigate} />;
    case 'portfolio':
      return block.projects.length ? (
        <SectionCard key={block.id} title={title} subtitle={labels.projectsCount(block.projects.length)} icon={<BriefcaseBusiness size={20} strokeWidth={1.8} />} density={density}>
          <div style={{ display: 'grid', gap: 1, background: BORDER_SUBTLE }}>{block.projects.map((project) => <ProjectRow key={project.id} project={project} document={document} block={block} density={density} onNavigate={onNavigate} />)}</div>
        </SectionCard>
      ) : null;
    case 'services':
      return block.services.length ? <PrimaryLinkCard key={block.id} document={document} block={block} title={title} subtitle={labels.servicesCount(block.services.length)} href={block.href || document.publicUrl} density={density} onNavigate={onNavigate} /> : null;
    case 'visit':
      return block.location ? (
        <SectionCard key={block.id} title={title} subtitle={block.location} icon={<MapPin size={20} strokeWidth={1.8} />} density={density}>
          {block.href ? <div style={{ paddingInline: cardDensity.bodyPaddingInline, paddingBlockEnd: cardDensity.bodyPaddingBottom }}><NavigateLink href={buildHref(document, block.href, { kind: 'block', source: 'visit', blockId: block.id, blockType: block.type })} onNavigate={onNavigate} style={{ display: 'inline-flex', width: '100%', minHeight: cardDensity.actionButtonMinHeight, alignItems: 'center', justifyContent: 'center', borderRadius: PREVIEW_ACTION_RADIUS, border: `${PREVIEW_ACTION_BORDER_WIDTH} solid ${PRIMARY_BORDER}`, background: PRIMARY, color: PRIMARY_TEXT, textDecoration: 'none', fontSize: cardDensity.actionButtonFontSize, fontWeight: 700 }}>{labels.getDirections}</NavigateLink></div> : null}
        </SectionCard>
      ) : null;
    case 'map':
      return block.location ? (
        variant === 'preview' && block.href ? (
          <NavigateLink
            key={block.id}
            href={buildHref(document, block.href, {
              kind: 'block',
              source: 'map',
              blockId: block.id,
              blockType: block.type,
            })}
            onNavigate={onNavigate}
            label={labels.openMap}
            style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}
          >
            <CardShell density={density}>
              <MapPreview location={block.location} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: cardDensity.sectionHeaderGap, padding: cardDensity.sectionHeaderPadding }}>
                <div dir="auto" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: TEXT_TERTIARY, fontSize: cardDensity.sectionSubtitleFontSize }}>
                  {block.location}
                </div>
                <ExternalLink size={18} strokeWidth={1.9} />
              </div>
            </CardShell>
          </NavigateLink>
        ) : (
          <CardShell density={density} key={block.id}>
            <MapPreview location={block.location} interactive />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: cardDensity.sectionHeaderGap, padding: cardDensity.sectionHeaderPadding }}>
              <div dir="auto" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: TEXT_SECONDARY, fontSize: cardDensity.sectionSubtitleFontSize }}>{block.location}</div>
              {block.href ? <NavigateLink href={buildHref(document, block.href, { kind: 'block', source: 'map', blockId: block.id, blockType: block.type })} onNavigate={onNavigate} label={labels.openMap} style={{ width: cardDensity.mapActionButtonSize, height: cardDensity.mapActionButtonSize, borderRadius: 999, display: 'grid', placeItems: 'center', flexShrink: 0, border: `1px solid ${BORDER}`, background: SURFACE_SECONDARY, color: TEXT }}><ExternalLink size={18} strokeWidth={1.9} /></NavigateLink> : null}
            </div>
          </CardShell>
        )
      ) : null;
    // A list of labelled destinations. Rows with no URL are dropped rather than
    // rendered as dead text: the block exists to send people somewhere.
    case 'links': {
      const linkItems = block.items.filter((item) => item.url);
      return linkItems.length ? (
        <SectionCard key={block.id} title={title} subtitle={block.subtitle} icon={<LinkIcon size={20} strokeWidth={1.8} />} density={density}>
          <div style={{ display: 'grid', gap: 1, background: BORDER_SUBTLE }}>
            {linkItems.map((item) => (
              <NavigateLink
                key={item.id}
                href={buildHref(document, item.url as string, { kind: 'block', source: 'links', blockId: block.id, blockType: block.type })}
                onNavigate={onNavigate}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: cardDensity.sectionHeaderPadding, background: SURFACE, color: TEXT, textDecoration: 'none' }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div dir="auto" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: cardDensity.sectionTitleFontSize, fontWeight: 600 }}>{item.title || displayUrl(item.url as string)}</div>
                  {item.title ? <div dir="auto" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: TEXT_SECONDARY, fontSize: cardDensity.sectionSubtitleFontSize }}>{displayUrl(item.url as string)}</div> : null}
                </div>
                <ExternalLink size={16} strokeWidth={1.9} style={{ flexShrink: 0, color: TEXT_SECONDARY }} />
              </NavigateLink>
            ))}
          </div>
        </SectionCard>
      ) : null;
    }
    case 'header':
    case 'text':
      return block.type === 'text' && !block.body ? null : (
        <SectionCard key={block.id} title={title} subtitle={block.subtitle} density={density}>
          {block.type === 'text' ? <div style={{ paddingInline: cardDensity.bodyPaddingInline, paddingBlockEnd: cardDensity.bodyPaddingBottom }}><p dir="auto" style={{ margin: 0, whiteSpace: 'pre-wrap', color: TEXT_SECONDARY, fontSize: cardDensity.sectionSubtitleFontSize, lineHeight: cardDensity.sectionSubtitleLineHeight }}>{block.body}</p></div> : null}
        </SectionCard>
      );
    case 'form':
      return !block.href && !block.body ? null : (
        <SectionCard key={block.id} title={title || labels.contactForm} subtitle={block.body} density={density}>
          {block.href ? <div style={{ paddingInline: cardDensity.bodyPaddingInline, paddingBlockEnd: cardDensity.bodyPaddingBottom }}><NavigateLink href={buildHref(document, block.href, { kind: 'block', source: 'form', blockId: block.id, blockType: block.type })} onNavigate={onNavigate} style={{ display: 'inline-flex', width: '100%', minHeight: cardDensity.actionButtonMinHeight, alignItems: 'center', justifyContent: 'center', borderRadius: PREVIEW_ACTION_RADIUS, border: `${PREVIEW_ACTION_BORDER_WIDTH} solid ${PRIMARY_BORDER}`, background: PRIMARY, color: PRIMARY_TEXT, textDecoration: 'none', fontSize: cardDensity.actionButtonFontSize, fontWeight: 700 }}>{block.buttonLabel || labels.openForm}</NavigateLink></div> : null}
        </SectionCard>
      );
    case 'openroles':
      return <PublicProfileOpenRolesSection key={block.id} document={document} block={block} labels={labels} density={density} onNavigate={onNavigate} onRoleMore={onRoleMore} onTalentPool={onTalentPool} />;
    case 'team':
      return block.members.length ? (
        <SectionCard key={block.id} id={getSectionAnchor(block.type)} title={title} subtitle={labels.teamCount(block.members.length)} icon={<Users size={20} strokeWidth={1.8} />} density={density}>
          <div style={{ display: 'grid', gap: 1, background: BORDER_SUBTLE }}>
            {block.members.map((member) => (
              <div key={member.id} style={{ display: 'flex', alignItems: 'center', gap: cardDensity.projectRowGap, background: SURFACE_ELEVATED, paddingBlock: cardDensity.projectRowPaddingBlock, paddingInline: cardDensity.projectRowPaddingInline }}>
                <div style={{ width: cardDensity.teamAvatarSize, height: cardDensity.teamAvatarSize, borderRadius: 999, overflow: 'hidden', flexShrink: 0, display: 'grid', placeItems: 'center', background: SURFACE_SECONDARY, color: TEXT_SECONDARY, fontWeight: 700 }}>{member.imageUrl ? <img src={member.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : getAvatarFallback(member.name)}</div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div dir="auto" style={{ color: TEXT, fontSize: cardDensity.teamNameFontSize, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.name}</div>
                  <div dir="auto" style={{ color: TEXT_SECONDARY, fontSize: cardDensity.teamRoleFontSize, marginBlockStart: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.role}</div>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      ) : null;
    default:
      return null;
  }
}

function PublicProfileFooter({
  document,
  labels,
  onFooterCta,
  privacyHref,
  density,
}: {
  document: PublicProfileSurfaceDocument;
  labels: PublicProfileSurfaceLabels;
  onFooterCta?: () => void;
  privacyHref?: string;
  density: PublicProfileSurfaceDensity;
}) {
  const footerDensity = density.footer;
  const resolvedPrivacyHref = privacyHref?.trim() || '/privacy-policy';
  return (
    <footer style={{ borderBlockStart: `1px solid ${BORDER_SUBTLE}`, paddingBlock: `${footerDensity.paddingBlockStart}px ${footerDensity.paddingBlockEnd}px`, textAlign: 'center' }}>
      {onFooterCta ? (
        <button
          type="button"
          onClick={onFooterCta}
          style={{
            display: 'inline-flex',
            minHeight: footerDensity.ctaMinHeight,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: footerDensity.ctaRadius,
            border: `1px solid ${BORDER}`,
            background: 'rgb(246 246 246)',
            color: TEXT,
            paddingInline: footerDensity.ctaPaddingInline,
            fontSize: footerDensity.ctaFontSize,
            fontWeight: 650,
            font: 'inherit',
            cursor: 'pointer',
          }}
        >
          {labels.joinOwner(document.owner.name)}
        </button>
      ) : (
        <a href="/" style={{ display: 'inline-flex', minHeight: footerDensity.ctaMinHeight, alignItems: 'center', justifyContent: 'center', borderRadius: footerDensity.ctaRadius, border: `1px solid ${BORDER}`, background: SURFACE_ELEVATED, color: TEXT, paddingInline: footerDensity.ctaPaddingInline, textDecoration: 'none', fontSize: footerDensity.ctaFontSize, fontWeight: 650 }}>
          {labels.joinOwner(document.owner.name)}
        </a>
      )}
      <div style={{ marginBlockStart: footerDensity.linksMarginTop, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: footerDensity.linksGap, color: TEXT_TERTIARY, fontSize: footerDensity.linksFontSize }}>
        <span>{labels.cookiePreferences}</span>
        <span>{labels.report}</span>
        <a href={resolvedPrivacyHref} style={{ color: 'inherit', textDecoration: 'none' }}>
          {labels.privacy}
        </a>
      </div>
    </footer>
  );
}

const srOnlyStyle: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export function PublicProfileSurface({
  document,
  previewDesign,
  labels,
  variant = 'published',
  topSlot,
  upgradeSlot,
  beforeHeroSlot,
  editSlot,
  footerSlot,
  footerPrivacyHref,
  onNavigate,
  onOpenMenu,
  onUpgrade,
  onFooterCta,
  onRoleMore,
  onTalentPool,
  dir,
  className,
  style,
}: PublicProfileSurfaceProps) {
  const density = SURFACE_DENSITY[variant];
  const resolvedPreviewDesign =
    variant === 'preview' && previewDesign
      ? resolvePublicProfilePreviewDesign(previewDesign)
      : null;
  return (
    <main
      className={className}
      dir={dir}
      style={{
        minHeight: '100vh',
        overflowX: 'hidden',
        background: SURFACE,
        color: TEXT,
        ...(resolvedPreviewDesign?.cssVariables as CSSProperties),
        ...style,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 950,
          marginInline: 'auto',
          paddingInline: density.shellPaddingInline,
          paddingBlockStart: density.shellPaddingTop,
        }}
      >
        {topSlot ?? (variant === 'preview' ? <PublicProfileTopChrome document={document} labels={labels} density={density} upgradeSlot={upgradeSlot} onOpenMenu={onOpenMenu} onUpgrade={onUpgrade} /> : null)}
        {beforeHeroSlot}
        <PublicProfileHero document={document} labels={labels} density={density} onNavigate={onNavigate} />
        {document.blocks.length ? (
          <div style={{ display: 'grid', gap: density.contentGap, paddingBlockEnd: density.contentPaddingBottom }}>
            {document.blocks.map((block) => renderBlockSection(block, document, labels, variant, density, onNavigate, onRoleMore, onTalentPool))}
          </div>
        ) : null}
        {footerSlot ?? (
          <PublicProfileFooter
            document={document}
            labels={labels}
            density={density}
            onFooterCta={onFooterCta}
            privacyHref={footerPrivacyHref}
          />
        )}
      </div>
      {editSlot}
    </main>
  );
}
